"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { track, isOwnerBrowser } from "@/lib/track";
import { analyticsStorage } from "@/lib/consent-region";

/**
 * Global page view tracker. Drop into the root layout once
 * and every page navigation fires a Supabase event.
 *
 * Extracts section, slug, and human-readable page name from the URL
 * so the admin dashboard can break down traffic by section.
 *
 * Also captures:
 * - Referrer and UTM parameters on first page view (traffic source/medium)
 * - Session start time so we can compute session duration later
 * - Fires a "session_end" beacon when the tab closes (with time-on-site)
 */
/**
 * Client-side bot detection v2. Scores multiple signals and returns
 * both a boolean (likely bot?) and a numeric score for query-time filtering.
 *
 * Score 0 = definitely human, 100 = definitely bot.
 * Threshold of 40+ = treated as bot and skipped client-side.
 * Events below 40 still carry their score in meta.bot_score so you
 * can filter borderline traffic in Supabase queries.
 */
const BOT_THRESHOLD = 40;

function getBotScore(): { isBot: boolean; score: number; signals: string[] } {
  let score = 0;
  const signals: string[] = [];

  try {
    // ── Hard signals (very strong indicators) ──

    // navigator.webdriver is true in Puppeteer, Playwright, Selenium
    if (navigator.webdriver) { score += 80; signals.push("webdriver"); }

    // PhantomJS fingerprints
    if ((window as any).__phantomas || (window as any)._phantom || (window as any).phantom) {
      score += 80; signals.push("phantom");
    }

    // Automation frameworks inject global variables
    if ((window as any).__selenium_unwrapped || (window as any).__fxdriver_unwrapped ||
        (window as any)._Selenium_IDE_Recorder || (window as any).callSelenium ||
        (window as any).__nightmare || (window as any).domAutomation ||
        (window as any).domAutomationController) {
      score += 80; signals.push("automation_globals");
    }

    // CDP (Chrome DevTools Protocol) runtime detection
    if ((window as any).cdc_adoQpoasnfa76pfcZLmcfl_Array ||
        (window as any).cdc_adoQpoasnfa76pfcZLmcfl_Promise ||
        (window as any).cdc_adoQpoasnfa76pfcZLmcfl_Symbol) {
      score += 70; signals.push("cdp_runtime");
    }

    // ── Medium signals (suspicious but not conclusive alone) ──

    // No plugins AND no languages = headless or stripped browser
    if (navigator.plugins && navigator.plugins.length === 0 && !navigator.languages?.length) {
      score += 30; signals.push("no_plugins_no_langs");
    }

    // Zero-dimension viewport (headless browsers often have 0x0 or very small)
    if (window.innerWidth === 0 || window.innerHeight === 0) {
      score += 40; signals.push("zero_viewport");
    }

    // Suspiciously tiny viewport (less than 200px in either dimension)
    if (window.innerWidth > 0 && window.innerWidth < 200 || window.innerHeight > 0 && window.innerHeight < 200) {
      score += 20; signals.push("tiny_viewport");
    }

    // No screen dimensions (real browsers always have this)
    if (!screen.width || !screen.height) {
      score += 30; signals.push("no_screen");
    }

    // Mismatch: screen smaller than viewport (impossible in real browsers)
    if (screen.width > 0 && window.outerWidth > 0 && window.outerWidth > screen.width * 1.5) {
      score += 25; signals.push("viewport_exceeds_screen");
    }

    // No browser history capability (real browsers always have at least 1)
    if (typeof history === "undefined" || history.length === 0) {
      score += 15; signals.push("no_history");
    }

    // Missing or empty user agent
    const ua = navigator.userAgent || "";
    if (!ua || ua.length < 10) {
      score += 30; signals.push("empty_ua");
    }

    // Chrome without Chrome-specific APIs
    if (ua.includes("Chrome") && !(window as any).chrome) {
      score += 20; signals.push("fake_chrome");
    }

    // ── Soft signals (weak individually, meaningful in combination) ──

    // No touch support on a mobile UA (spoofed mobile)
    if (/Mobile|Android|iPhone/i.test(ua) && !("ontouchstart" in window) && navigator.maxTouchPoints === 0) {
      score += 15; signals.push("fake_mobile");
    }

    // Notification permission already denied before any prompt (some bot frameworks)
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "denied" && !document.hasFocus()) {
        score += 10; signals.push("notif_denied_no_focus");
      }
    } catch {}

    // Connection API reports no connection type (headless often lacks this)
    try {
      const conn = (navigator as any).connection;
      if (conn && !conn.effectiveType) {
        score += 10; signals.push("no_connection_type");
      }
    } catch {}

  } catch {
    // If detection itself throws, environment is suspect
    score += 50;
    signals.push("detection_error");
  }

  return { isBot: score >= BOT_THRESHOLD, score: Math.min(score, 100), signals };
}

/** Cached result so we only compute once per page load */
let _botResult: { isBot: boolean; score: number; signals: string[] } | null = null;
function getCachedBotResult() {
  if (!_botResult) _botResult = getBotScore();
  return _botResult;
}

// Keep backward-compatible function name for existing checks
function isClientBot(): boolean {
  return getCachedBotResult().isBot;
}

export default function PageTracker() {
  const pathname = usePathname();
  const lastTracked = useRef("");
  const sessionStarted = useRef(false);
  const scrollMilestones = useRef<Set<number>>(new Set());

  // Capture referrer & UTM params once per session, and fire session_end on unload
  useEffect(() => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;

    // Skip bots that execute JS (Puppeteer, Playwright, etc.)
    if (isClientBot()) return;

    // Don't track admin or API pages — that's just you checking the dashboard
    const landingPath = window.location.pathname;
    if (landingPath.startsWith("/admin") || landingPath.startsWith("/api")) return;

    // Detect new vs returning visitor
    let isNewVisitor = false;
    try {
      const firstVisit = analyticsStorage.get("local", "tf_first_visit");
      if (!firstVisit) {
        analyticsStorage.set("local", "tf_first_visit", new Date().toISOString());
        isNewVisitor = true;
      }
    } catch {}

    // Store session start time
    const sessionStart = Date.now();
    try { analyticsStorage.set("session", "tf_session_start", String(sessionStart)); } catch {}

    // Grab referrer and UTM params
    const referrer = document.referrer || "";
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_term: params.get("utm_term") || "",
      utm_content: params.get("utm_content") || "",
    };

    // Click markers that Google, Facebook, Microsoft and others add to their
    // links. Only the marker's name is kept, never its value, so nothing here
    // can identify the person. They name the source when the referrer is
    // missing, as with links opened from apps (October 11, 2026).
    const clickIds = CLICK_MARKERS.filter((k) => params.has(k));

    // Determine traffic source from referrer
    const trafficSource = categorizeReferrer(referrer, utm.utm_source, utm.utm_medium, clickIds);

    // Store for use by other tracking events
    try {
      analyticsStorage.set("session", "tf_referrer", referrer);
      analyticsStorage.set("session", "tf_traffic_source", trafficSource.source);
      analyticsStorage.set("session", "tf_traffic_medium", trafficSource.medium);
    } catch {}

    // Include bot score in session for query-time filtering
    const botResult = getCachedBotResult();

    // Fire a session_start event with referrer/UTM data
    track({
      event: "session_start",
      source: "page_tracker",
      meta: {
        referrer: referrer || "direct",
        traffic_source: trafficSource.source,
        traffic_medium: trafficSource.medium,
        is_new_visitor: isNewVisitor,
        bot_score: botResult.score,
        ...(botResult.signals.length > 0 ? { bot_signals: botResult.signals.join(",") } : {}),
        ...(utm.utm_source ? { utm_source: utm.utm_source } : {}),
        ...(utm.utm_medium ? { utm_medium: utm.utm_medium } : {}),
        ...(utm.utm_campaign ? { utm_campaign: utm.utm_campaign } : {}),
        ...(utm.utm_term ? { utm_term: utm.utm_term } : {}),
        ...(utm.utm_content ? { utm_content: utm.utm_content } : {}),
        ...(clickIds.length ? { click_ids: clickIds.join(",") } : {}),
        landing_page: window.location.pathname,
      },
    });

    // Reading time, raw. The browser records when the reader was present and
    // how long each pause lasted; the server decides what counts
    // (activeSecondsOf in lib/analytics-core.ts). Keeping the rule on the
    // server means a better rule can be rerun on every visit that sent raw
    // pieces (timing_v 2). The first version sent only a finished
    // number, which locked its 60-second rule into the data.
    //
    // A "stretch" is time the page is on screen and the browser window has
    // focus. It starts on load, on coming back to the tab, on focusing the
    // window or on any action. Inside a stretch, the time between 2 actions
    // (scroll, wheel, tap, click, key, mouse move) is a pause:
    //   - pauses of 30 seconds or less are summed into busy_s
    //   - longer pauses are listed in pauses_s, in seconds
    // The time from the last action to the end of a stretch (tab hidden,
    // window blurred, page closed) is a tail: 30 seconds or less goes into
    // busy_s, longer tails are listed in tails_s. Hidden or unfocused time
    // is never recorded.
    const SHORT_MS = 30_000;
    const LONGEST_S = 1800;
    const MAX_LISTED = 120;
    let busyMs = 0;
    const pauses: number[] = [];
    const tails: number[] = [];
    let inStretch = document.visibilityState === "visible";
    let lastMark = Date.now();
    function closeGap(now: number, kind: "pause" | "tail") {
      const gap = now - lastMark;
      if (gap <= 0) return;
      if (gap <= SHORT_MS) { busyMs += gap; return; }
      const list = kind === "pause" ? pauses : tails;
      if (list.length < MAX_LISTED) list.push(Math.min(Math.round(gap / 1000), LONGEST_S));
    }
    function startStretch() {
      if (inStretch || document.visibilityState !== "visible") return;
      inStretch = true;
      lastMark = Date.now();
    }
    function endStretch() {
      if (!inStretch) return;
      closeGap(Date.now(), "tail");
      inStretch = false;
    }
    function markActive() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (inStretch) closeGap(now, "pause");
      else inStretch = true;
      lastMark = now;
    }
    const activityEvents = ["scroll", "pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;
    let lastMove = 0;
    function onActivity(e: Event) {
      // Mouse moves arrive many times a second; one per 5 seconds is plenty.
      if (e.type === "mousemove") {
        const now = Date.now();
        if (now - lastMove < 5000) return;
        lastMove = now;
      }
      markActive();
    }
    for (const ev of activityEvents) window.addEventListener(ev, onActivity, { passive: true });
    // Switching to another app with the page still on screen ends the
    // stretch. Clicking into an embedded frame also blurs the window, but the
    // reader is still on the page, so that case keeps counting.
    function onBlur() {
      window.setTimeout(() => {
        if (document.activeElement?.tagName === "IFRAME") return;
        if (!document.hasFocus()) endStretch();
      }, 0);
    }
    function onFocus() { startStretch(); }
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    // On tab close / navigate away / tab hide, fire session_end with duration.
    // beforeunload is unreliable on mobile (iOS Safari, Chrome Android swipe-away),
    // so we listen on visibilitychange + pagehide and guard with a "sent" flag.
    let sent = false;
    function handleUnload() {
      // Closing or leaving the page ends the current stretch.
      endStretch();
      if (sent) return;
      sent = true;
      if (isOwnerBrowser()) return; // the owner's browsers send nothing
      try {
        const rawDuration = Math.round((Date.now() - sessionStart) / 1000);
        // duration_seconds is the original open-tab time, kept for comparison
        // with visits before reading time existed (September 29, 2026, 8 PM ET).
        const duration = Math.min(rawDuration, 600);
        let pagesViewed = 1;
        try { pagesViewed = parseInt(analyticsStorage.get("session", "tf_pages_viewed") || "1", 10); } catch {}
        // Include deviceId so the server can exclude owner traffic
        let deviceId = "";
        try { deviceId = analyticsStorage.get("local", "tf_device_id") || ""; } catch {}
        const payload = JSON.stringify({
          event: "session_end",
          source: "page_tracker",
          url: window.location.pathname,
          deviceId,
          meta: {
            duration_seconds: duration,
            timing_v: 2,
            busy_s: Math.round(busyMs / 1000),
            pauses_s: pauses.slice(),
            tails_s: tails.slice(),
            pages_viewed: pagesViewed,
            traffic_source: trafficSource.source,
            traffic_medium: trafficSource.medium,
          },
        });
        // Prefer sendBeacon (survives tab close); fall back to keepalive fetch.
        let delivered = false;
        if (navigator.sendBeacon) {
          try {
            delivered = navigator.sendBeacon("/api/track", payload);
          } catch {}
        }
        if (!delivered) {
          try {
            fetch("/api/track", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: payload,
              keepalive: true,
            }).catch(() => {});
          } catch {}
        }
      } catch {}
    }

    // visibilitychange fires when the tab is hidden (backgrounded, switched away).
    // On mobile this is often the ONLY event you get before the tab dies.
    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        endStretch();
        handleUnload();
      }
    }
    // If the user comes back to the tab, allow a fresh session_end later.
    // (Rare for real users; helps during dev and long-open tabs.)
    function handleVisibilityRestore() {
      if (document.visibilityState === "visible") {
        sent = false;
        // Hidden time never counts; coming back starts a new stretch.
        startStretch();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("visibilitychange", handleVisibilityRestore);
    window.addEventListener("pagehide", handleUnload);
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("visibilitychange", handleVisibilityRestore);
      window.removeEventListener("pagehide", handleUnload);
      window.removeEventListener("beforeunload", handleUnload);
      for (const ev of activityEvents) window.removeEventListener(ev, onActivity);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Track page views
  useEffect(() => {
    if (!pathname || pathname.startsWith("/admin") || pathname.startsWith("/api")) return;
    if (isClientBot()) return;
    if (pathname === lastTracked.current) return;
    lastTracked.current = pathname;

    // Reset scroll milestones for new page
    scrollMilestones.current = new Set();

    // Increment pages viewed counter
    try {
      const current = parseInt(analyticsStorage.get("session", "tf_pages_viewed") || "0", 10);
      analyticsStorage.set("session", "tf_pages_viewed", String(current + 1));
    } catch {}

    const parts = pathname.split("/").filter(Boolean);
    const section = parts[0] || "home";
    const slug = parts[1] || undefined;
    const source = getSectionSource(section, slug);

    // Include traffic source in page view meta for richer analytics
    let trafficSource = "";
    let trafficMedium = "";
    try {
      trafficSource = analyticsStorage.get("session", "tf_traffic_source") || "";
      trafficMedium = analyticsStorage.get("session", "tf_traffic_medium") || "";
    } catch {}

    track({
      event: "page_view",
      disease: slug || undefined,
      source,
      meta: {
        section,
        path: pathname,
        ...(slug ? { slug } : {}),
        ...(trafficSource ? { traffic_source: trafficSource } : {}),
        ...(trafficMedium ? { traffic_medium: trafficMedium } : {}),
      },
    });
  }, [pathname]);

  // Scroll depth tracking — fire at 25%, 50%, 75%, 100%
  useEffect(() => {
    if (!pathname || pathname.startsWith("/admin") || pathname.startsWith("/api")) return;
    if (isClientBot()) return;

    const milestones = [25, 50, 75, 100];

    function handleScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);

      for (const m of milestones) {
        if (pct >= m && !scrollMilestones.current.has(m)) {
          scrollMilestones.current.add(m);
          track({
            event: "scroll_depth",
            source: "page_tracker",
            meta: {
              path: pathname,
              depth: m,
            },
          });
        }
      }
    }

    // Use passive listener and throttle with rAF
    let ticking = false;
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => { handleScroll(); ticking = false; });
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
}

function getSectionSource(section: string, slug?: string): string {
  switch (section) {
    case "home":
      return "homepage";
    case "trials":
      return slug ? "disease_page" : "trials_directory";
    case "companies":
      return slug ? "company_page" : "companies_directory";
    case "blog":
      return slug ? "blog_post" : "blog_index";
    case "learn":
      return slug ? "learn_article" : "learn_index";
    case "symptoms":
      return "symptom_finder";
    case "genes":
      return "gene_search";
    case "match":
      return "match_me";
    case "drugs":
      return slug ? "drug_page" : "drug_directory";
    case "diseases":
      return "disease_directory";
    case "do-not-sell":
      return "do_not_sell_page";
    case "preferences":
      return "preferences_page";
    case "faq":
      return "faq_page";
    case "about":
      return "about_page";
    case "privacy":
      return "privacy_page";
    case "terms":
      return "terms_page";
    default:
      return section;
  }
}

/** Click-marker URL parameters, and the source each one names. The server
 *  keeps its own copy of this map in lib/visit-source.ts. */
const CLICK_SOURCE: Record<string, { source: string; medium: string }> = {
  srsltid: { source: "google", medium: "organic" },
  gclid: { source: "google", medium: "cpc" },
  gbraid: { source: "google", medium: "cpc" },
  wbraid: { source: "google", medium: "cpc" },
  msclkid: { source: "bing", medium: "cpc" },
  fbclid: { source: "facebook", medium: "social" },
  igshid: { source: "instagram", medium: "social" },
  igsh: { source: "instagram", medium: "social" },
  ttclid: { source: "tiktok", medium: "social" },
  twclid: { source: "twitter", medium: "social" },
  li_fat_id: { source: "linkedin", medium: "social" },
};
const CLICK_MARKERS = Object.keys(CLICK_SOURCE);

/**
 * Categorize a referrer URL into a human-readable source/medium pair.
 */
function categorizeReferrer(
  referrer: string,
  utmSource: string,
  utmMedium: string,
  clickIds: string[] = []
): { source: string; medium: string } {
  // If UTM params are set, use those
  if (utmSource) {
    return { source: utmSource, medium: utmMedium || "unknown" };
  }

  if (!referrer) {
    const marked = clickIds.map((k) => CLICK_SOURCE[k]).find(Boolean);
    if (marked) return marked;
    return { source: "direct", medium: "none" };
  }

  try {
    const host = new URL(referrer).hostname.toLowerCase();

    // Search engines
    if (host.includes("google.")) return { source: "google", medium: "organic" };
    if (host.includes("bing.")) return { source: "bing", medium: "organic" };
    if (host.includes("yahoo.")) return { source: "yahoo", medium: "organic" };
    if (host.includes("duckduckgo.")) return { source: "duckduckgo", medium: "organic" };
    if (host.includes("baidu.")) return { source: "baidu", medium: "organic" };

    // Social media
    // Whole host names only. A substring test for "t.co" matched reddit.com
    // and microsoft.com, so a Reddit visit on October 12, 2026 was stored as
    // Twitter; "x.com" and "fb." had the same problem in other hosts.
    if (/(^|\.)facebook\.com$/.test(host) || /(^|\.)fb\.(com|me)$/.test(host)) return { source: "facebook", medium: "social" };
    if (/(^|\.)twitter\.com$/.test(host) || host === "t.co" || /^(www\.|mobile\.)?x\.com$/.test(host)) return { source: "twitter", medium: "social" };
    if (host.includes("linkedin.")) return { source: "linkedin", medium: "social" };
    if (host.includes("reddit.")) return { source: "reddit", medium: "social" };
    if (host.includes("instagram.")) return { source: "instagram", medium: "social" };
    if (host.includes("tiktok.")) return { source: "tiktok", medium: "social" };
    if (host.includes("youtube.")) return { source: "youtube", medium: "social" };
    if (host.includes("pinterest.")) return { source: "pinterest", medium: "social" };

    // Same domain = a new session that began on a Trial Friend page (a link
    // opened in a new tab, an idle tab coming back). Labeled internal, not
    // direct, so Direct only counts people arriving from outside (Oct 11, 2026).
    if (host.includes("trialfriend.com")) return { source: "internal", medium: "internal" };

    // Everything else is a referral
    return { source: host.replace("www.", ""), medium: "referral" };
  } catch {
    return { source: "unknown", medium: "unknown" };
  }
}
